'use client';

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useMachine } from '@xstate/react';
import {
  WordleGrid,
  type Tile,
  WordleNavbar,
  WordleKeyboard,
  GameOverModal,
  SettingsModal,
  WinModal,
  HowToPlayModal,
  DatePickerModal,
  HardcoreTimer,
  HardcoreFrame,
} from '@/modules/wordle-6/components';
import { createWordleMachine } from '@wordle/game-engine/wordle.machine';
import { Toaster } from 'sonner';
import { launchConfetti } from '@/hooks/useConfetti';
import { LocalStorageHistoryStore } from '@/history/localStorage.store';
import { LocalStorageGameSaveStore } from '@/history/game-save.store';
import { StatsModal } from '@/modules/wordle-6/components/StatsModal';
import { useWordleFeedback } from '@/hooks/useWordleFeedback';
import { useHardcoreTimer } from '@/hooks/useHardcoreTimer';
import { FeedbackBubble } from '@/modules/wordle-6/components/FeedbackBubble';
import { AnimatePresence, motion } from 'framer-motion';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { AZERTY_TO_QWERTY, type KeyboardLayout } from '@/utils/keyboardLayouts';
import { getDailyWordFromSolutions, WORDLE_VERSION } from '@/utils/getDailyWord';
import { FR_WORDLE_STRINGS, type WordleStrings } from '@/modules/wordle-6/wordleStrings';

const ROWS = 6;
const COLS = 5;
const HARDCORE_DURATION_MS = 105_000;
const RED_CONFETTI = ['#ef4444', '#dc2626', '#b91c1c'];

function getDateKey(date: Date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

export type WordleGameConfig = {
  solutions: string[];
  acceptedWords: string[];
  storageNamespace?: string;
  dateLocale?: string;
  keyboardLayoutDefault?: KeyboardLayout;
  srDescription: string;
  strings?: WordleStrings;
};

function hardcorePrefKey(namespace?: string) {
  return `wordle6:hardcore:${namespace ?? 'default'}`;
}

function readHardcorePref(namespace?: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(hardcorePrefKey(namespace)) === 'true';
  } catch {
    return false;
  }
}

function writeHardcorePref(namespace: string | undefined, value: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      hardcorePrefKey(namespace),
      value ? 'true' : 'false'
    );
  } catch {
    // Stockage indisponible : on ignore, la préférence reste en mémoire.
  }
}

/**
 * Lecture SYNCHRONE et en lecture seule : la journée est-elle terminée
 * (gagné/perdu) dans ce namespace ? Miroir de la clé de
 * `LocalStorageHistoryStore` (`wordle6:<version>:history[:namespace]`) —
 * pas de migration ni d'écriture (safe en rendu).
 */
function isDayFinishedInNamespace(
  namespace: string | undefined,
  dateKey: string
): boolean {
  if (typeof window === 'undefined') return false;
  const key = namespace
    ? `wordle6:${WORDLE_VERSION}:history:${namespace}`
    : `wordle6:${WORDLE_VERSION}:history`;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return false;
    const entry = parsed.find(
      (e: { dateKey?: string; status?: string }) => e?.dateKey === dateKey
    );
    return !!entry && entry.status !== 'in-progress';
  } catch {
    return false;
  }
}

/**
 * Wrapper mince : gère la préférence hardcore (persistée) et **remonte** la
 * session de jeu via `key={namespace effectif}` quand on bascule de mode.
 * La piste hardcore utilise un namespace localStorage dédié (`<ns>-hc`), donc
 * ses stores + sa machine sont totalement isolés de la partie normale.
 * Aucune donnée existante (`<ns>`, legacy) n'est jamais touchée.
 */
export function WordleGame({ config }: { config: WordleGameConfig }) {
  // `hardcorePref` = préférence persistée (le clic sur le toggle).
  const [hardcorePref, setHardcorePref] = useState(false);
  // La date est détenue par le wrapper (qui ne se remonte pas) afin d'être
  // préservée quand on bascule de mode — sinon le remontage de la session
  // réinitialiserait la date à aujourd'hui.
  const [date, setDate] = useState(new Date());

  // Lecture de la préférence au montage (côté client → pas de mismatch SSR).
  useEffect(() => {
    if (readHardcorePref(config.storageNamespace)) {
      setHardcorePref(true);
    }
  }, [config.storageNamespace]);

  const toggleHardcore = () => {
    setHardcorePref(prev => {
      const next = !prev;
      writeHardcorePref(config.storageNamespace, next);
      return next;
    });
  };

  // Le mot du jour étant identique dans les deux modes, une journée déjà
  // terminée (dans l'un OU l'autre mode) s'affiche en vue normale : on
  // désactive le hardcore pour CE jour sans toucher à la préférence (elle
  // se réactive dès qu'on revient sur un jour non terminé). Lecture
  // synchrone → pas de flash de cadre rouge.
  const dayFinished = useMemo(() => {
    const dateKey = getDateKey(date);
    const base = config.storageNamespace;
    return (
      isDayFinishedInNamespace(base, dateKey) ||
      isDayFinishedInNamespace(`${base ?? ''}-hc`, dateKey)
    );
  }, [date, config.storageNamespace]);

  const hardcore = hardcorePref && !dayFinished;

  // Préserver `undefined` en mode normal (ne pas coercer → ne pas changer la
  // clé du store normal).
  const effectiveNamespace = hardcore
    ? `${config.storageNamespace ?? ''}-hc`
    : config.storageNamespace;

  return (
    <WordleSession
      key={effectiveNamespace ?? 'base'}
      config={config}
      namespace={effectiveNamespace}
      hardcore={hardcore}
      onToggleHardcore={toggleHardcore}
      date={date}
      onDateChange={setDate}
    />
  );
}

function WordleSession({
  config,
  namespace,
  hardcore,
  onToggleHardcore,
  date,
  onDateChange: setDate,
}: {
  config: WordleGameConfig;
  namespace?: string;
  hardcore: boolean;
  onToggleHardcore: () => void;
  date: Date;
  onDateChange: Dispatch<SetStateAction<Date>>;
}) {
  const strings = config.strings ?? FR_WORDLE_STRINGS;
  const [isGameOverOpen, setIsGameOverOpen] = useState(false);
  const [pressedKey, setPressedKey] = useState<string | null>(null);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [isWinModalOpen, setIsWinModalOpen] = useState(false);
  const [isHowToPlayOpen, setIsHowToPlayOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [statsRefreshKey, setStatsRefreshKey] = useState(0);
  const [justFailed, setJustFailed] = useState(false);
  // Différé au montage : évite un mismatch d'hydratation pour le contenu qui
  // dépend de lectures localStorage (indicateur "joué en hardcore").
  const [hasMounted, setHasMounted] = useState(false);
  const [keyboardLayout, setKeyboardLayout] = useState<KeyboardLayout>(
    config.keyboardLayoutDefault ?? 'AZERTY'
  );

  const hasOpenedWinModal = useRef(false);
  const hasFiredLoss = useRef(false);
  const isHydratingRef = useRef(true);
  const prevRowRef = useRef(0);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { feedback, triggerFeedbackFromRow } = useWordleFeedback(strings.feedbacks);

  const historyStore = useMemo(
    () => new LocalStorageHistoryStore(namespace),
    [namespace]
  );

  const saveStore = useMemo(
    () => new LocalStorageGameSaveStore(namespace),
    [namespace]
  );

  // Historique de l'AUTRE mode. Comme le mot du jour est identique en normal
  // et en hardcore, terminer le mot dans un mode le verrouille dans l'autre
  // (aucun intérêt à rejouer un mot déjà connu).
  const otherNamespace = hardcore
    ? config.storageNamespace
    : `${config.storageNamespace ?? ''}-hc`;
  const otherHistoryStore = useMemo(
    () => new LocalStorageHistoryStore(otherNamespace),
    [otherNamespace]
  );

  const machine = useMemo(
    () =>
      createWordleMachine({
        rows: ROWS,
        cols: COLS,
        historyStore,
        saveStore,
        acceptedWords: config.acceptedWords,
        hardcoreTimeLimitMs: HARDCORE_DURATION_MS,
      }),
    [historyStore, saveStore, config.acceptedWords]
  );

  const [state, send] = useMachine(machine);

  useEffect(() => {
    const dateKey = getDateKey(date);

    isHydratingRef.current = true;
    prevRowRef.current = 0;

    const finishHydration = (row: number) => {
      setTimeout(() => {
        isHydratingRef.current = false;
        prevRowRef.current = row;
      }, 0);
    };

    void (async () => {
      // 1. Partie déjà TERMINÉE dans CE mode → on l'affiche (verrouillée).
      const ownHistory = await historyStore.get(dateKey);
      if (ownHistory && ownHistory.status !== 'in-progress') {
        send({
          type: 'START_GAME',
          dateKey,
          targetWord: ownHistory.targetWord,
          history: ownHistory,
          hardcore,
        });
        finishHydration(ownHistory.currentRow);
        return;
      }

      // 2. Verrou inter-modes : mot déjà terminé dans l'AUTRE mode (même mot
      //    du jour). PRIORITAIRE sur une partie en cours de ce mode → on
      //    n'autorise pas à rejouer/finir un mot déjà connu.
      const other = await otherHistoryStore.get(dateKey);
      if (other && other.status !== 'in-progress') {
        send({
          type: 'START_GAME',
          dateKey,
          targetWord: other.targetWord,
          history: other,
          hardcore,
        });
        finishHydration(other.currentRow);
        return;
      }

      // 3. Reprise d'une partie EN COURS de ce mode (sauvegarde par date).
      const savedState = saveStore.load(dateKey);
      if (savedState && savedState.dateKey === dateKey) {
        const revealedCorrectLetters: Record<number, string> = {};
        savedState.revealedCorrectLetters.forEach((value, key) => {
          revealedCorrectLetters[key] = value;
        });
        const { rows, cols, ...restState } = savedState;
        send({
          type: 'START_GAME',
          dateKey,
          targetWord: savedState.targetWord,
          savedState: {
            ...restState,
            revealedCorrectLetters,
          },
        });
        finishHydration(savedState.currentRow);
        return;
      }

      // 4. Nouvelle partie (ou historique en cours sans sauvegarde).
      send({
        type: 'START_GAME',
        dateKey,
        targetWord:
          ownHistory?.targetWord ??
          getDailyWordFromSolutions(config.solutions, date),
        hardcore,
      });
      finishHydration(ownHistory?.currentRow ?? 0);
    })();
  }, [date, send, saveStore, historyStore, otherHistoryStore, config, hardcore]);

  // DEV uniquement : log le mot du jour de la date affichée (aide au test).
  // Jamais en production → ne divulgue pas les réponses sur le site public.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    const word = getDailyWordFromSolutions(config.solutions, date);
    console.log(`[wordle] ${getDateKey(date)} → ${word}`);
  }, [date, config.solutions]);

  function handleKeyInput(key: string) {
    setPressedKey(key);
    setTimeout(() => setPressedKey(null), 80);

    if (key === 'ENTER') {
      send({ type: 'SUBMIT_GUESS' });
      return;
    }

    if (key === 'BACKSPACE') {
      send({ type: 'BACKSPACE' });
      return;
    }

    send({ type: 'LETTER_TYPED', letter: key });
  }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (
        state.matches('idle') ||
        state.matches('won') ||
        state.matches('lost')
      ) {
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        handleKeyInput('BACKSPACE');
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        handleKeyInput('ENTER');
        return;
      }

      const rawKey = e.key.toUpperCase();

      if (!/^[A-Z]$/.test(rawKey)) return;

      let logicalKey = rawKey;

      if (keyboardLayout === 'QWERTY') {
        logicalKey = AZERTY_TO_QWERTY[rawKey] ?? rawKey;
      }

      handleKeyInput(logicalKey);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [state, keyboardLayout]);

  useEffect(() => {
    if (isHydratingRef.current) return;
    if (state.matches('won') || state.matches('lost')) return;

    const prevRow = prevRowRef.current;
    const currentRow = state.context.currentRow;

    if (currentRow === prevRow + 1) {
      const scoredRow = currentRow - 1;
      const row = state.context.grid[scoredRow];
      if (row) triggerFeedbackFromRow(row);
    }

    prevRowRef.current = currentRow;
  }, [state.context.currentRow, triggerFeedbackFromRow, state]);

  useEffect(() => {
    if (state.matches('won') && !hasOpenedWinModal.current) {
      hasOpenedWinModal.current = true;
      const isPerfect = state.context.currentRow === 0;
      launchConfetti({ perfect: isPerfect });
      setStatsRefreshKey(Date.now());
      setTimeout(() => setIsWinModalOpen(true), 1500);
    }
  }, [state]);

  useEffect(() => {
    if (state.matches('lost') && !hasFiredLoss.current) {
      hasFiredLoss.current = true;
      setStatsRefreshKey(Date.now());

      if (state.context.hardcore) {
        // Animation d'échec hardcore : paillettes rouges + flash, puis modale.
        setJustFailed(true);
        launchConfetti({
          colors: RED_CONFETTI,
          particleCount: 6,
          duration: 1500,
        });
        setTimeout(() => setJustFailed(false), 1000);
        setTimeout(() => setIsGameOverOpen(true), 900);
      } else {
        setIsGameOverOpen(true);
      }
    }
  }, [state]);

  useEffect(() => {
    hasOpenedWinModal.current = false;
    hasFiredLoss.current = false;
    setJustFailed(false);
  }, [date]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Timer hardcore basé sur une échéance ABSOLUE persistée (context.hardcoreDeadline) :
  // armée à la 1re proposition validée, elle survit reload/navigation → pas de
  // remise à zéro possible (anti-triche). Le timer est actif tant que la partie
  // n'est pas verrouillée et que la deadline est armée.
  const hardcoreDeadline = state.context.hardcoreDeadline ?? null;
  const timerActive =
    hardcore &&
    // Le contexte doit être hydraté pour le jour AFFICHÉ (évite d'afficher le
    // timer d'un autre jour pendant/juste après une navigation).
    state.context.dateKey === getDateKey(date) &&
    !state.context.locked &&
    // Une vraie partie en cours (au moins une proposition validée) : un
    // plateau vierge (ligne 0) ne montre jamais de timer.
    state.context.currentRow >= 1 &&
    hardcoreDeadline != null;

  // Une partie normale est-elle déjà ENTAMÉE (au moins une proposition validée
  // ou des lettres tapées par l'utilisateur — hors cases auto-révélées) ? Dans
  // ce cas on a déjà vu / joué le mot du jour → jouer en hardcore n'aurait plus
  // de sens.
  const normalGameStarted =
    state.context.currentRow >= 1 ||
    state.context.grid.some(row =>
      row.some(tile => tile.letter !== null && !tile.locked)
    );

  // Toggle Hardcore désactivé quand :
  //  - en mode normal, la partie du jour est déjà finie OU déjà entamée → on ne
  //    (re)lance pas le hardcore sur un mot déjà connu/joué ;
  //  - une partie hardcore est déjà démarrée (timer lancé, currentRow >= 1) et
  //    pas encore terminée → impossible de la quitter avant
  //    victoire/défaite/timeout.
  const hardcoreToggleDisabled =
    (!hardcore && (state.context.locked || normalGameStarted)) ||
    (hardcore && !state.context.locked && state.context.currentRow >= 1);

  const { remainingSeconds } = useHardcoreTimer({
    active: timerActive,
    deadline: hardcoreDeadline,
    onExpire: () => send({ type: 'TIME_UP' }),
  });

  const prevDay = () => {
    // Sauvegarde l'état actuel AVANT de changer la date
    // Utiliser state directement pour garantir qu'on a l'état le plus récent
    if (
      state.context.dateKey &&
      !state.matches('idle') &&
      !state.context.locked
    ) {
      // Vérifier qu'il y a vraiment une partie en cours (des lettres tapées)
      const hasLetters = state.context.grid.some(row =>
        row.some(tile => tile.letter !== null)
      );
      if (hasLetters) {
        // Sauvegarde synchrone immédiate
        saveStore.save(state.context);
      }
    }

    setDate(d => {
      const next = new Date(d);
      next.setDate(d.getDate() - 1);
      return next;
    });
  };

  const nextDay = () => {
    // Sauvegarde l'état actuel AVANT de changer la date
    // Utiliser state directement pour garantir qu'on a l'état le plus récent
    if (
      state.context.dateKey &&
      !state.matches('idle') &&
      !state.context.locked
    ) {
      // Vérifier qu'il y a vraiment une partie en cours (des lettres tapées)
      const hasLetters = state.context.grid.some(row =>
        row.some(tile => tile.letter !== null)
      );
      if (hasLetters) {
        // Sauvegarde synchrone immédiate
        saveStore.save(state.context);
      }
    }

    setDate(d => {
      const next = new Date(d);
      next.setDate(d.getDate() + 1);
      return next;
    });
  };

  const changeDate = (newDate: Date) => {
    // Sauvegarde l'état actuel AVANT de changer la date
    if (
      state.context.dateKey &&
      !state.matches('idle') &&
      !state.context.locked
    ) {
      // Vérifier qu'il y a vraiment une partie en cours (des lettres tapées)
      const hasLetters = state.context.grid.some(row =>
        row.some(tile => tile.letter !== null)
      );
      if (hasLetters) {
        // Sauvegarde synchrone immédiate
        saveStore.save(state.context);
      }
    }

    setDate(newDate);
  };

  const cursor = {
    row: state.context.currentRow,
    col: state.context.cursorCol,
  };

  const isPerfectWin =
    state.matches('won') && state.context.currentRow === 0;

  const isLostGame =
    state.context.locked &&
    state.context.targetWord !== '' &&
    !state.context.grid.some(
      row => row.map(t => t.letter ?? '').join('') === state.context.targetWord
    );

  // Le mot a-t-il été TROUVÉ et la partie VERROUILLÉE (proposition validée et
  // gagnante) ? Le `locked` est crucial : sans lui, la ligne en cours de saisie
  // qui épelle le mot cible (avant ENTER) passerait à tort en « gagné » → cadre
  // et code couleur verts prématurés. Sert au code couleur (vert = réussi).
  const hardcoreWon =
    state.context.locked &&
    state.context.targetWord !== '' &&
    state.context.grid.some(
      row => row.map(t => t.letter ?? '').join('') === state.context.targetWord
    );

  // Le JEU AFFICHÉ est-il une partie hardcore terminée ? (pour l'indicateur
  // sous-navbar + le chrono figé). Deux cas :
  //  - on vient de terminer en hardcore dans la session courante ;
  //  - le mot n'a PAS été terminé en normal mais l'a été en hardcore → c'est le
  //    jeu hardcore qui est affiché (verrou inter-modes).
  // Si le mot a été fait en NORMAL, on affiche le jeu normal → pas d'indicateur
  // hardcore, même si la piste hardcore a aussi une entrée (données de test).
  const hardcoreCompleted = useMemo(() => {
    if (!hasMounted) return false;
    if (hardcore && state.context.locked) return true;
    const dateKey = getDateKey(date);
    const base = config.storageNamespace;
    return (
      !hardcore &&
      !isDayFinishedInNamespace(base, dateKey) &&
      isDayFinishedInNamespace(`${base ?? ''}-hc`, dateKey)
    );
  }, [hasMounted, hardcore, state.context.locked, config.storageNamespace, date]);

  const dailyWord = useMemo(
    () => getDailyWordFromSolutions(config.solutions, date),
    [config.solutions, date]
  );

  // La bande de statut (timer hardcore / mot manqué) n'est montée que si elle a
  // un contenu → en mode normal la grille récupère toute la hauteur dispo.
  const showStatusBand =
    (timerActive && remainingSeconds !== null) ||
    (!timerActive && hardcoreCompleted) ||
    (isLostGame && !hardcoreCompleted);

  return (
    <>
      <Toaster position="top-center" />
      <p className="sr-only">{config.srDescription}</p>
      <HardcoreFrame active={hardcore} justFailed={justFailed} won={hardcoreWon} />
      {/* Secousse plein écran (navbar + grille + clavier) au moment de l'échec
          hardcore (timer écoulé ou 6 essais ratés). Piloté par `justFailed`. */}
      <motion.div
        className="flex h-dvh flex-col"
        animate={
          justFailed
            ? {
                x: [0, -14, 13, -11, 9, -7, 5, -3, 2, 0],
                y: [0, 5, -4, 4, -3, 2, -2, 1, 0, 0],
              }
            : { x: 0, y: 0 }
        }
        transition={
          justFailed ? { duration: 0.6, ease: 'easeInOut' } : { duration: 0 }
        }
      >
        <header className="sticky top-0 z-50">
          <WordleNavbar
            date={date}
            onPrevDay={prevDay}
            onNextDay={nextDay}
            autoRevealEnabled={state.context.autoRevealCorrectLetters}
            onToggleAutoReveal={() => send({ type: 'TOGGLE_AUTO_REVEAL' })}
            hardcoreEnabled={hardcore}
            onToggleHardcore={onToggleHardcore}
            hardcoreDisabled={hardcoreToggleDisabled}
            hardcoreCompleted={hardcoreCompleted}
            hardcoreWord={dailyWord}
            hardcoreWon={hardcoreWon}
            onOpenStats={() => setIsStatsOpen(true)}
            onOpenHowTo={() => setIsHowToPlayOpen(true)}
            onOpenSettings={() => setIsSettingsOpen(true)}
            onOpenDatePicker={() => setIsDatePickerOpen(true)}
            locale={config.dateLocale}
            strings={strings.navbar}
          />
        </header>

        <div className="flex min-h-0 flex-1 justify-center">
          <div className="flex h-full w-full max-w-[470px] flex-col">
            <main className="relative flex min-h-0 flex-1 flex-col items-center">
              <AnimatePresence>
                {!isMobile &&
                  feedback &&
                  !state.matches('won') &&
                  !state.matches('lost') && (
                    <FeedbackBubble key={feedback} text={feedback} />
                  )}
              </AnimatePresence>

              {showStatusBand && (
                <div className="flex w-full flex-col items-center justify-center gap-3 py-4">
                  {timerActive && remainingSeconds !== null && (
                    <HardcoreTimer
                      seconds={remainingSeconds}
                      label={strings.hardcore.timerLabel}
                    />
                  )}

                  {!timerActive && hardcoreCompleted && (
                    <HardcoreTimer
                      seconds={
                        hardcoreWon &&
                        state.context.hardcoreTimeRemainingMs != null
                          ? Math.ceil(
                              state.context.hardcoreTimeRemainingMs / 1000
                            )
                          : 0
                      }
                      label={strings.hardcore.timerLabel}
                      frozen
                      won={hardcoreWon}
                    />
                  )}

                  {isLostGame && !hardcoreCompleted && (
                    <p className="rounded-full border border-neutral-700 bg-neutral-900/80 px-3 py-2 text-sm text-neutral-200">
                      {strings.gameOver.wordWas}{' '}
                      <span className="font-bold text-red-400">
                        {state.context.targetWord}
                      </span>
                    </p>
                  )}
                </div>
              )}

              {/* La grille occupe TOUT l'espace restant entre la bande de statut
                  et le clavier, et sert de conteneur de requête (container query)
                  → les tuiles se dimensionnent (cqw/cqh) pour remplir cette zone,
                  plafonnées à 64px, sans jamais chevaucher le clavier qui reste
                  ancré en bas (footer). */}
              <div className="flex w-full min-h-0 flex-1 items-center justify-center px-2 py-2 [container-type:size]">
                <WordleGrid
                  grid={state.context.grid as Tile[][]}
                  cursor={cursor}
                  shakeRow={state.context.shakeRow}
                  dateKey={getDateKey(date)}
                />
              </div>
            </main>

            <footer className="shrink-0 pb-safe">
              <WordleKeyboard
                grid={state.context.grid as Tile[][]}
                currentRow={state.context.currentRow}
                pressedKey={pressedKey}
                onKeyPress={handleKeyInput}
                layout={keyboardLayout}
              />
            </footer>
          </div>
        </div>
      </motion.div>
      {isGameOverOpen && (
        <GameOverModal
          word={state.context.targetWord}
          onClose={() => setIsGameOverOpen(false)}
          isHardcore={hardcore}
          strings={
            hardcore
              ? {
                  ...strings.gameOver,
                  title: strings.hardcore.failTitle,
                  subline: strings.hardcore.failSubline,
                }
              : strings.gameOver
          }
        />
      )}
      {isStatsOpen && (
        <StatsModal
          onClose={() => setIsStatsOpen(false)}
          storageNamespace={namespace}
          strings={strings.stats}
          refreshKey={statsRefreshKey}
          isHardcore={hardcore}
        />
      )}
      {isWinModalOpen && (
        <WinModal
          onClose={() => setIsWinModalOpen(false)}
          isPerfectWin={isPerfectWin}
          isHardcore={hardcore}
          strings={strings.win}
          statsStrings={strings.stats}
          storageNamespace={namespace}
          refreshKey={statsRefreshKey}
        />
      )}
      {isHowToPlayOpen && (
        <HowToPlayModal
          onClose={() => setIsHowToPlayOpen(false)}
          strings={strings.howToPlay}
        />
      )}
      {isSettingsOpen && (
        <SettingsModal
          keyboardLayout={keyboardLayout}
          onToggleKeyboard={() =>
            setKeyboardLayout(l => (l === 'AZERTY' ? 'QWERTY' : 'AZERTY'))
          }
          onClose={() => setIsSettingsOpen(false)}
          strings={strings.settings}
        />
      )}
      {isDatePickerOpen && (
        <DatePickerModal
          currentDate={date}
          onSelectDate={changeDate}
          onClose={() => setIsDatePickerOpen(false)}
          normalNamespace={config.storageNamespace}
          hardcoreNamespace={`${config.storageNamespace ?? ''}-hc`}
          preferHardcore={hardcore}
          strings={strings.datePicker}
        />
      )}
    </>
  );
}
